import React, { useRef, useState, Fragment } from 'react';
import type { FC } from 'react';
import cn from 'classnames';
import { OfficeMIcon } from '@alfalab/icons-glyph/OfficeMIcon';
import { InformationCircleLineSIcon } from '@alfalab/icons-glyph/InformationCircleLineSIcon';
import { Grid } from '@alfa-bank/site-grid-universal';
import type {
    IProps as IFormSelectProps,
    TOption,
} from '@alfa-bank/site-form-select/src/models/common';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import { deliveryStepAnalytics as analytics } from '../../../../../../analytics';

import type { TOfficeSelection, TFormSelectOnChange } from './models';
import { prepareDataToSelector, sortOfficesByPid } from './helpers';

export const OfficeSelection: FC<TOfficeSelection> = ({
    Select,
    Text,
    isMobile,
    offices,
    selectedOffice: officeSettings,
    styles,
}) => {
    const prevSelected = useRef<TOption | null>(null);
    const [selectedOfficePid, setSelectedOfficePid] = useState<string>('');

    const isOpened = useRef(false);

    const onChange: TFormSelectOnChange = data => {
        const [selected] = data;

        if (selected) {
            setSelectedOfficePid(selected?.key);

            // FormSelectMobile при выборе вызывает onChange дважды
            // проверка, чтобы не отправлять дубль аналитики
            if (selected !== prevSelected.current) {
                sendEvent(analytics.officeDelivery.choice);
            }
            prevSelected.current = selected;
        }
    };

    const onOpen = () => {
        isOpened.current = !isOpened.current;
        if (isOpened.current) {
            sendEvent(analytics.officeDelivery.focus);
        }
    };

    const sortedOffices = sortOfficesByPid(offices);
    const selectedOffice = selectedOfficePid ? sortedOffices[selectedOfficePid] : undefined;

    return (
        <>
            <Text
                styler={{ marginBottom: isMobile ? 16 : 24, marginTop: isMobile ? 16 : 40 }}
                view={isMobile ? 'xsmall' : 'small'}
            >
                Выберите офис банка
            </Text>
            <Select
                name="selectedOffice"
                onChange={onChange as IFormSelectProps['onChange']}
                onOpen={onOpen}
                optionsListWidth="field"
                settings={{
                    options: prepareDataToSelector(offices),
                    block: true,
                    placeholder: 'Офис Альфа-Банка',
                    progressBarValue: officeSettings?.progressBarValue,
                }}
                size="m"
                validationRules={{
                    isRequired: {
                        errorMessage: 'Поле обязательно для заполнения',
                    },
                }}
            />

            {selectedOfficePid && selectedOffice && (
                <Grid
                    className={cn(styles.external, styles.background)}
                    colAutoWidth
                    collapse
                    columnsMargin={8}
                    justify="start"
                    styler={{ padding: 24, borderRadius: isMobile ? 16 : 24, marginTop: 16 }}
                >
                    <OfficeMIcon />

                    <>
                        <Text
                            dataTestId="dc-form-selected-office"
                            styler={{ margin: 0, padding: 0, marginBottom: 2 }}
                            view="accent-primary-medium"
                        >
                            {selectedOffice.title}
                        </Text>

                        {selectedOffice.metro &&
                            selectedOffice?.metro.slice(0, 3).map(curr => (
                                <Grid key={curr.id} alignment colAutoWidth collapse justify="start">
                                    <div
                                        className={cn(styles.external, styles.metroIcon)}
                                        style={{ backgroundColor: `#${curr.lineColor}` }}
                                    ></div>
                                    <Text
                                        kind="caption"
                                        styler={{ margin: 0, padding: 0 }}
                                        view="secondary-large"
                                    >
                                        {curr.title}
                                    </Text>
                                </Grid>
                            ))}

                        <Text
                            kind="caption"
                            styler={{ margin: 0, padding: 0, marginBottom: 0, marginTop: 2 }}
                            view="secondary-large"
                        >
                            {selectedOffice.address}
                        </Text>

                        <Grid collapse columnsWidth={[174, 'auto']} justify="start" wrap>
                            <>
                                <Text
                                    styler={{
                                        marginTop: isMobile ? 16 : 24,
                                        margin: 0,
                                        padding: 0,
                                        marginBottom: 4,
                                    }}
                                    view="accent-primary-small"
                                >
                                    Время работы:
                                </Text>
                                {selectedOffice?.schedule?.map((day, index) => {
                                    const isElementLast =
                                        index === selectedOffice?.schedule?.length - 1;

                                    return (
                                        <Fragment key={day.day}>
                                            <Text
                                                kind="caption"
                                                styler={{ margin: 0, padding: 0, marginBottom: 2 }}
                                                view="secondary-large"
                                            >
                                                {day.day}
                                            </Text>
                                            <Text
                                                styler={{
                                                    margin: 0,
                                                    padding: 0,
                                                    marginBottom: isElementLast ? 0 : 8,
                                                }}
                                                view="secondary-large"
                                            >
                                                {day.time}
                                            </Text>
                                        </Fragment>
                                    );
                                })}
                            </>
                            {selectedOffice?.constraints.length > 0 && (
                                <Grid
                                    className={cn(styles.external, styles.constraints)}
                                    colAutoWidth
                                    collapse
                                    columnsMargin={4}
                                    justify="start"
                                    styler={{ marginTop: isMobile ? 16 : 24 }}
                                >
                                    <InformationCircleLineSIcon color="#ef3124" />
                                    <>
                                        <Text
                                            styler={{
                                                margin: 0,
                                                padding: 0,
                                                marginBottom: 4,
                                            }}
                                            view="accent-primary-small"
                                        >
                                            Изменения в расписании:
                                        </Text>

                                        {selectedOffice?.constraints.map((el, index) => {
                                            const isElementLast =
                                                index === selectedOffice?.constraints.length - 1;

                                            return (
                                                <Fragment key={el.date}>
                                                    <Text
                                                        dataTestId={`dc-form-offices-constraints-${index}-date`}
                                                        kind="caption"
                                                        styler={{
                                                            margin: 0,
                                                            padding: 0,
                                                            marginBottom: 2,
                                                        }}
                                                        view="secondary-large"
                                                    >
                                                        {el.date}
                                                    </Text>
                                                    <Text
                                                        dataTestId={`dc-form-offices-constraints-${index}-time`}
                                                        styler={{
                                                            margin: 0,
                                                            padding: 0,
                                                            marginBottom: isElementLast ? 0 : 8,
                                                        }}
                                                        view="secondary-large"
                                                    >
                                                        {el?.time}
                                                    </Text>
                                                </Fragment>
                                            );
                                        })}
                                    </>
                                </Grid>
                            )}
                        </Grid>
                    </>
                </Grid>
            )}
        </>
    );
};
